import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CheckCircle2, ArrowUpRight, ArrowLeft } from "lucide-react";
import LeadDialog from "../components/LeadDialog";
import MobileCaptureDialog from "../components/MobileCaptureDialog";
import ShareButton from "../components/ShareButton";
import { withUtm, trackClick, getSessionId, getStoredUTM, getUserId, setUserId } from "../lib/utm";
import { getInviteSlug, getInviteTag } from "../lib/invite";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [openLead, setOpenLead] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(null));
  }, [id]);

  if (!product) {
    return <div className="max-w-6xl mx-auto px-6 py-16 text-neutral-500" data-testid="product-loading">Loading product…</div>;
  }

  const hasIdentity = () => {
    const uid = getUserId();
    return uid && uid.startsWith("HITCH-USER-");
  };

  const doTrackedRedirect = async () => {
    try {
      const inviteSlug = getInviteSlug();
      const inviteTag = getInviteTag();
      const { data } = await api.post("/track/click", {
        product_id: product.id,
        session_id: getSessionId(),
        user_id: getUserId() || undefined,
        ...getStoredUTM(),
        referrer: document.referrer || "",
        current_url: window.location.href,
        ...(inviteSlug ? { invite_slug: inviteSlug } : {}),
        ...(inviteSlug && inviteTag ? { invite_tag: inviteTag } : {}),
      });
      if (data?.user_id) setUserId(data.user_id);
      const url = data?.tracked_redirect_url || withUtm(product.redirect_url);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      if (product.redirect_url) window.open(withUtm(product.redirect_url), "_blank", "noopener,noreferrer");
    }
  };

  const handleCta = async () => {
    // Banking products always require a mobile number before we generate the UTM
    if (product.type === "banking" && !hasIdentity()) {
      setOpenMobile(true);
      return;
    }
    if (product.lead_mode === "redirect_only") {
      await doTrackedRedirect();
      return;
    }
    setOpenLead(true);
  };

  const isBanking = product.type === "banking";

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      <Link to="/marketplace" data-testid="back-to-marketplace" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-black mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to marketplace
      </Link>

      <div className="grid md:grid-cols-2 gap-10">
        <div className="aspect-square rounded-xl overflow-hidden bg-neutral-100">
          {product.image_url && <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />}
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="badge-mono">{isBanking ? "Banking" : "Commerce"}</Badge>
            <Badge variant="outline" className="badge-mono">{product.category.replace("_", " ")}</Badge>
          </div>
          <div className="text-sm uppercase tracking-[0.2em] text-neutral-500 mb-2">{product.provider}</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-none">
            {product.title}
          </h1>
          {product.subtitle && <p className="mt-4 text-lg text-neutral-600">{product.subtitle}</p>}

          {!isBanking && product.price && (
            <div className="mt-6 flex items-end gap-3">
              <div className="font-display text-4xl font-bold tracking-tight">₹{product.price.toLocaleString('en-IN')}</div>
              {product.original_price && (
                <>
                  <div className="text-lg text-neutral-400 line-through mb-1">₹{product.original_price.toLocaleString('en-IN')}</div>
                  <Badge className="bg-[#00A36C] text-white hover:bg-[#00A36C]">Save ₹{(product.original_price - product.price).toLocaleString('en-IN')}</Badge>
                </>
              )}
            </div>
          )}

          {isBanking && (
            <div className="mt-6 grid grid-cols-2 gap-3">
              {product.apr && (
                <div className="bg-white border border-black/8 rounded-lg p-4">
                  <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">APR</div>
                  <div className="font-display text-2xl font-bold tracking-tight mt-1">{product.apr}</div>
                </div>
              )}
              {product.interest_rate && (
                <div className="bg-white border border-black/8 rounded-lg p-4">
                  <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Interest</div>
                  <div className="font-display text-2xl font-bold tracking-tight mt-1">{product.interest_rate}</div>
                </div>
              )}
              {product.tenure && (
                <div className="bg-white border border-black/8 rounded-lg p-4 col-span-2">
                  <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Tenure</div>
                  <div className="font-display text-lg font-semibold tracking-tight mt-1">{product.tenure}</div>
                </div>
              )}
            </div>
          )}

          <ul className="mt-6 space-y-2">
            {(product.features || []).map((f) => (
              <li key={f} className="flex items-start gap-2 text-neutral-700">
                <CheckCircle2 className="w-5 h-5 mt-0.5 text-[#00A36C] flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8">
            <Button
              data-testid="detail-cta-btn"
              onClick={handleCta}
              className="h-12 px-8 bg-black hover:bg-[#0055FF] text-white text-base font-medium cta-cursor"
            >
              {product.cta_label || (isBanking ? "Apply Now" : "Shop Now")}
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
            <div className="mt-3 max-w-xs">
              <ShareButton product={product} />
            </div>
            <p className="text-xs text-neutral-400 mt-3 font-mono-accent">
              Attribution enabled · UTM tracked · Session id linked
            </p>
          </div>
        </div>
      </div>

      {product.description && (
        <div className="mt-16 max-w-3xl">
          <div className="badge-mono text-neutral-500 mb-3">Overview</div>
          <p className="text-neutral-700 leading-relaxed text-lg">{product.description}</p>
        </div>
      )}

      <LeadDialog open={openLead} onOpenChange={setOpenLead} product={product} />
      <MobileCaptureDialog
        open={openMobile}
        onOpenChange={setOpenMobile}
        product={product}
        onCaptured={async () => {
          if (product.lead_mode === "redirect_only") {
            await doTrackedRedirect();
          } else {
            setOpenLead(true);
          }
        }}
      />
    </div>
  );
}
